import { ReactElement, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { publicAPI, ministriesAPI } from '../../services/api';
import { useGrievance } from '../../contexts/GrievanceContexts';

// ─── Types ─────────────────────────────────────────────────────────────────────
interface MinistryStats {
  id: number;
  name: string;
  total: number;
  pending: number;
  inProgress: number;
  resolved: number;
  rejected: number;
}

// ─── Icon pool — assigned by ministry index ────────────────────────────────────
const ICONS = ['🏛️', '🎓', '🏥', '💰', '🌾', '🛣️', '⚡', '🗂️', '🌳', '👷', '👩‍👧', '🏭', '⚖️', '✈️', '🛡️', '📡'];
const GRADIENTS = [
  'from-blue-500 to-blue-700',
  'from-emerald-500 to-emerald-700',
  'from-violet-500 to-violet-700',
  'from-amber-500 to-amber-700',
  'from-lime-500 to-lime-700',
  'from-orange-500 to-orange-700',
  'from-cyan-500 to-cyan-700',
  'from-pink-500 to-pink-700',
  'from-green-500 to-green-700',
  'from-red-500 to-red-700',
  'from-purple-500 to-purple-700',
  'from-teal-500 to-teal-700',
  'from-indigo-500 to-indigo-700',
  'from-rose-500 to-rose-700',
  'from-sky-500 to-sky-700',
  'from-yellow-500 to-yellow-700',
];
const color = [
  '#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#84cc16', '#f97316',
  '#06b6d4', '#ec4899', '#22c55e', '#ef4444', '#a855f7', '#14b8a6',
  '#6366f1', '#f43f5e', '#0ea5e9', '#eab308',
];


// ─── Progress Bar ──────────────────────────────────────────────────────────────
function ProgressBar({ value, total, color }: { value: number; total: number; color: string }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
      <div
        className="h-1.5 rounded-full transition-all duration-700"
        style={{ width: `${pct}%`, backgroundColor: color }}
      />
    </div>
  );
}

// ─── Donut Chart ───────────────────────────────────────────────────────────────
function DonutChart({
  segments, total, colors, labels, title,
}: {
  segments: number[]; total: number; colors: string[]; labels: string[]; title: string;
}) {
  const radius = 60;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;
  const normalized = total > 0
    ? segments.map(v => (v / total) * circumference)
    : segments.map(() => 0);

  return (
    <div className="bg-white rounded-xl shadow-md p-5 border border-gray-100">
      <div className="text-sm font-semibold text-gray-500 mb-3 uppercase tracking-wide">{title}</div>
      <div className="flex items-center gap-6">
        <svg width="140" height="140" viewBox="0 0 160 160">
          <g transform="translate(80,80)">
            {normalized.map((len, i) => {
              const circle = (
                <circle
                  key={i}
                  r={radius}
                  cx={0}
                  cy={0}
                  fill="transparent"
                  stroke={colors[i]}
                  strokeWidth={22}
                  strokeDasharray={`${len} ${circumference - len}`}
                  strokeDashoffset={-offset}
                />
              );
              offset += len;
              return circle;
            })}
            <circle r={42} cx={0} cy={0} fill="#fff" />
            <text textAnchor="middle" dy="-4" fontSize="13" fill="#111827" fontWeight="700">{total}</text>
            <text textAnchor="middle" dy="14" fontSize="9" fill="#6b7280">Total</text>
          </g>
        </svg>
        <div className="space-y-2 flex-1">
          {segments.map((v, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              <span className="inline-block w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: colors[i] }} />
              <span className="text-gray-500 flex-1">{labels[i]}</span>
              <span className="font-bold text-gray-800">{v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Ministry Card ─────────────────────────────────────────────────────────────
function MinistryCard({ ministry, index }: { ministry: MinistryStats; index: number }) {
  const resolvedPct = ministry.total > 0 ? Math.round((ministry.resolved / ministry.total) * 100) : 0;
  const gradient = GRADIENTS[index % GRADIENTS.length];
  const icon = ICONS[index % ICONS.length];

  return (
    <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
      {/* Gradient Header */}
      <div className={`bg-gradient-to-r ${gradient} p-5`}>
        <div className="flex items-center gap-3">
          <div className="text-4xl">{icon}</div>
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-bold text-sm leading-snug line-clamp-2">{ministry.name}</h3>
          </div>
        </div>
        <div className="mt-4 flex items-end justify-between">
          <div>
            <div className="text-white/70 text-xs">Total Grievances</div>
            <div className="text-white text-3xl font-extrabold leading-none">{ministry.total}</div>
          </div>
          <div className="text-right">
            <div className="text-white/70 text-xs">Resolution Rate</div>
            <div className="text-white text-2xl font-bold">{resolvedPct}%</div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="p-5 space-y-3">
        <div>
          <div className="flex justify-between text-xs mb-1">
            <span className="flex items-center gap-1.5 font-medium text-amber-700">
              <span className="w-2 h-2 rounded-full bg-amber-400 inline-block" /> Pending
            </span>
            <span className="font-bold text-amber-800">{ministry.pending}</span>
          </div>
          <ProgressBar value={ministry.pending} total={ministry.total} color="#f59e0b" />
        </div>
        <div>
          <div className="flex justify-between text-xs mb-1">
            <span className="flex items-center gap-1.5 font-medium text-blue-700">
              <span className="w-2 h-2 rounded-full bg-blue-400 inline-block" /> In Progress
            </span>
            <span className="font-bold text-blue-800">{ministry.inProgress}</span>
          </div>
          <ProgressBar value={ministry.inProgress} total={ministry.total} color="#3b82f6" />
        </div>
        <div>
          <div className="flex justify-between text-xs mb-1">
            <span className="flex items-center gap-1.5 font-medium text-emerald-700">
              <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block" /> Resolved
            </span>
            <span className="font-bold text-emerald-800">{ministry.resolved}</span>
          </div>
          <ProgressBar value={ministry.resolved} total={ministry.total} color="#10b981" />
        </div>
        <div>
          <div className="flex justify-between text-xs mb-1">
            <span className="flex items-center gap-1.5 font-medium text-red-700">
              <span className="w-2 h-2 rounded-full bg-red-400 inline-block" /> Rejected
            </span>
            <span className="font-bold text-red-800">{ministry.rejected}</span>
          </div>
          <ProgressBar value={ministry.rejected} total={ministry.total} color="#ef4444" />
        </div>
      </div>
    </div>
  );
}

// ─── Skeleton Loader ───────────────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden animate-pulse">
      <div className="h-32 bg-gray-200" />
      <div className="p-5 space-y-3">
        {[1, 2, 3, 4].map(i => (
          <div key={i}>
            <div className="flex justify-between mb-1">
              <div className="h-3 bg-gray-200 rounded w-20" />
              <div className="h-3 bg-gray-200 rounded w-6" />
            </div>
            <div className="h-1.5 bg-gray-200 rounded-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────
const PublicStatsPage = (): ReactElement => {
  const {
    totalGrievance, resolvedGrievances, pendingGrievances,
    inProgressGrievances, resolutionRate, lowPriorityCount,
    mediumPriorityCount, highPriorityCount,
  } = useGrievance();

  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'total' | 'pending' | 'resolved' | 'inProgress'>('total');

  // ── Live public stats (overall numbers) ──────────────────────────────────────
  const { data: publicStatsData,} = useQuery({
    queryKey: ['public-stats'],
    queryFn: async () => {
      const res = await publicAPI.getPublicStats();
      return res.data;
    },
  });

  // ── Ministry-wise stats from backend ─────────────────────────────────────────
  const { data: ministryStatsData, isLoading: ministryLoading, isError: ministryError } = useQuery({
    queryKey: ['ministry-stats'],
    queryFn: async () => {
      const res = await ministriesAPI.getStats();
      return res.data as { data: MinistryStats[] };
    },
    staleTime: 60_000,
  });

  const stats = useMemo(() => {
    return publicStatsData?.data || {
      total: totalGrievance,
      resolved: resolvedGrievances,
      pending: pendingGrievances,
      inProgressed: inProgressGrievances,
      resolutionRate: resolutionRate,
      averageRating: 0,
      priority: {
        LOW: lowPriorityCount,
        MEDIUM: mediumPriorityCount,
        HIGH: highPriorityCount,
      },
    };
  }, [publicStatsData, totalGrievance]);

  const statusSegments = [Number(stats.pending), Number(stats.inProgressed), Number(stats.resolved)];
  const statusLabels = ['Pending', 'In Progress', 'Resolved'];
  const statusColors = ['#f59e0b', '#3b82f6', '#10b981'];

  const prioritySegments = [
    Number(stats.priority?.LOW || 0),
    Number(stats.priority?.MEDIUM || 0),
    Number(stats.priority?.HIGH || 0),
  ];
  const priorityLabels = ['Low', 'Medium', 'High'];
  const priorityColors = ['#60a5fa', '#fbbf24', '#ef4444'];
  const totalPriority = prioritySegments.reduce((a, c) => a + c, 0);
  const totalGrievanceCount = statusSegments.reduce((a, c) => a + c, 0);

  // ── Real ministry data ────────────────────────────────────────────────────────
  const allMinistries: MinistryStats[] = ministryStatsData?.data || [];

  // Grand totals from real data
  const grandTotals = useMemo(() => ({
    total: allMinistries.reduce((a, m) => a + m.total, 0),
    pending: allMinistries.reduce((a, m) => a + m.pending, 0),
    inProgress: allMinistries.reduce((a, m) => a + m.inProgress, 0),
    resolved: allMinistries.reduce((a, m) => a + m.resolved, 0),
    rejected: allMinistries.reduce((a, m) => a + m.rejected, 0),
  }), [allMinistries]);

  // Filtered + sorted
  const filteredMinistries = useMemo(() => {
    return allMinistries
      .filter(m => m.name.toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => b[sortBy] - a[sortBy]);
  }, [allMinistries, search, sortBy]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <div
        className="relative py-16 px-4 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #1d4ed8 50%, #0891b2 100%)' }}
      >
        <div className="absolute top-0 left-0 w-72 h-72 bg-white/5 rounded-full -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/5 rounded-full translate-x-1/3 translate-y-1/3" />

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-10">
            <span className="inline-block bg-white/10 text-white text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-widest mb-4 backdrop-blur-sm border border-white/20">
              Transparency Dashboard
            </span>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-3 tracking-tight">
              Public Grievance Statistics
            </h1>
            <p className="text-blue-200 text-lg max-w-2xl mx-auto">
              Live overview of grievances across Bagamati Province.
            </p>
          </div>

          {/* Grand Total Stat Cards */}
          {ministryLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="bg-white/10 rounded-2xl p-4 animate-pulse h-20" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {[
                { label: 'Total', value: grandTotals.total, color: 'bg-white/20', textColor: 'text-white' },
                { label: 'Pending', value: grandTotals.pending, color: 'bg-amber-500/30', textColor: 'text-amber-200' },
                { label: 'In Progress', value: grandTotals.inProgress, color: 'bg-blue-500/30', textColor: 'text-blue-200' },
                { label: 'Resolved', value: grandTotals.resolved, color: 'bg-emerald-500/30', textColor: 'text-emerald-200' },
                { label: 'Rejected', value: grandTotals.rejected, color: 'bg-red-500/30', textColor: 'text-red-200' },
              ].map(item => (
                <div
                  key={item.label}
                  className={`${item.color} backdrop-blur-sm rounded-2xl p-4 border border-white/10 text-center`}
                >
                  <div className={`text-3xl font-extrabold ${item.textColor}`}>{item.value.toLocaleString()}</div>
                  <div className="text-white/70 text-xs mt-1 font-medium uppercase tracking-wide">{item.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Analysis Charts ────────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-1">Grievance Analysis</h2>
          <p className="text-gray-500 text-sm">Distribution by status and priority</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <DonutChart
            segments={statusSegments}
            total={totalGrievanceCount}
            colors={statusColors}
            labels={statusLabels}
            title="Grievances by Status"
          />
          <DonutChart
            segments={prioritySegments}
            total={totalPriority}
            colors={priorityColors}
            labels={priorityLabels}
            title="Grievances by Priority"
          />
          <div className="bg-white rounded-xl shadow-md p-5 border border-gray-100">
            <div className="text-sm font-semibold text-gray-500 mb-3 uppercase tracking-wide">Key Metrics</div>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Resolution Rate</span>
                  <span className="font-bold text-emerald-700">
                    {stats.resolutionRate?.toFixed ? stats.resolutionRate.toFixed(1) : 0}%
                  </span>
                </div>
                <ProgressBar value={Math.round(Number(stats.resolutionRate) || 0)} total={100} color="#10b981" />
              </div>
              <div className="pt-2 border-t border-gray-100">
                <div className="text-gray-600 text-sm mb-1">Avg. Citizen Satisfaction</div>
                <div className="flex items-end gap-2">
                  <span className="text-3xl font-extrabold text-gray-900">
                    {stats.averageRating?.toFixed ? stats.averageRating.toFixed(1) : '0.0'}
                  </span>
                  <span className="text-gray-400 mb-1">/ 5.0</span>
                </div>
                <div className="flex gap-0.5 mt-1">
                  {[1, 2, 3, 4, 5].map(star => (
                    <span key={star} className={`text-lg ${star <= Math.round(Number(stats.averageRating) || 0) ? 'text-amber-400' : 'text-gray-200'}`}>★</span>
                  ))}
                </div>
              </div>
              <div className="pt-2 border-t border-gray-100">
                <div className="text-gray-600 text-sm">Ministries Tracked</div>
                <div className="text-2xl font-bold text-gray-800 mt-0.5">
                  {ministryLoading ? '—' : allMinistries.length}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── All in One Data ────────────────────────────────────────────────── */}
        <div>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">All in One Data</h2>
              <p className="text-gray-500 text-sm mt-0.5">
                Ministry-wise grievance breakdown — total, pending, in-progress &amp; resolved
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
                <input
                  id="ministry-search"
                  type="text"
                  placeholder="Search ministry..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white shadow-sm w-56"
                />
              </div>
              <select
                id="ministry-sort"
                value={sortBy}
                onChange={e => setSortBy(e.target.value as any)}
                className="border border-gray-200 rounded-lg text-sm px-3 py-2 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="total">Sort by Total</option>
                <option value="pending">Sort by Pending</option>
                <option value="inProgress">Sort by In Progress</option>
                <option value="resolved">Sort by Resolved</option>
              </select>
            </div>
          </div>

          {/* Error State */}
          {ministryError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center text-red-600 mb-6">
              <div className="text-3xl mb-2">⚠️</div>
              <div className="font-semibold">Failed to load ministry data</div>
              <div className="text-sm mt-1 text-red-500">Please check if the backend server is running.</div>
            </div>
          )}

          {/* Desktop Table */}
          {!ministryLoading && !ministryError && filteredMinistries.length > 0 && (
            <div className="hidden lg:block mb-8 bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="text-left px-5 py-3 font-semibold text-gray-500 uppercase text-xs tracking-wider">#</th>
                    <th className="text-left px-5 py-3 font-semibold text-gray-500 uppercase text-xs tracking-wider">Ministry</th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-500 uppercase text-xs tracking-wider">Total</th>
                    <th className="text-center px-4 py-3 font-semibold text-amber-600 uppercase text-xs tracking-wider">Pending</th>
                    <th className="text-center px-4 py-3 font-semibold text-blue-600 uppercase text-xs tracking-wider">In Progress</th>
                    <th className="text-center px-4 py-3 font-semibold text-emerald-600 uppercase text-xs tracking-wider">Resolved</th>
                    <th className="text-center px-4 py-3 font-semibold text-red-500 uppercase text-xs tracking-wider">Rejected</th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-500 uppercase text-xs tracking-wider">Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMinistries.map((m, idx) => {
                    const rate = m.total > 0 ? Math.round((m.resolved / m.total) * 100): 0;
                    const icon = ICONS[idx % ICONS.length];
                    const barColor = color[idx % color.length];
                    return (
                      <tr key={m.id} className="border-b border-gray-50 hover:bg-gray-50/80 transition-colors">
                        <td className="px-5 py-3.5 text-gray-400 font-medium">{idx + 1}</td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-2.5">
                            <span className="text-xl">{icon}</span>
                            <div className="font-semibold text-gray-800 text-sm">{m.name}</div>
                          </div>
                        </td>
                        <td className="px-4 py-3.5 text-center font-bold text-gray-900">{m.total}</td>
                        <td className="px-4 py-3.5 text-center">
                          <span className="inline-block bg-amber-50 text-amber-700 font-bold px-2.5 py-0.5 rounded-full text-xs">{m.pending}</span>
                        </td>
                        <td className="px-4 py-3.5 text-center">
                          <span className="inline-block bg-blue-50 text-blue-700 font-bold px-2.5 py-0.5 rounded-full text-xs">{m.inProgress}</span>
                        </td>
                        <td className="px-4 py-3.5 text-center">
                          <span className="inline-block bg-emerald-50 text-emerald-700 font-bold px-2.5 py-0.5 rounded-full text-xs">{m.resolved}</span>
                        </td>
                        <td className="px-4 py-3.5 text-center">
                          <span className="inline-block bg-red-50 text-red-700 font-bold px-2.5 py-0.5 rounded-full text-xs">{m.rejected}</span>
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-100 rounded-full h-1.5 w-16">
                              <div className="h-1.5 rounded-full" style={{ width: `${rate}%`, backgroundColor: barColor }} />
                            </div>
                            <span className="text-xs font-bold" style={{ color: barColor }}>{rate}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {/* Grand Total Row */}
                  <tr className="bg-gray-900 text-white">
                    <td className="px-5 py-4" colSpan={2}>
                      <div className="font-bold text-sm">📊 Grand Total — All Ministries</div>
                    </td>
                    <td className="px-4 py-4 text-center font-extrabold text-lg">{grandTotals.total}</td>
                    <td className="px-4 py-4 text-center font-bold text-amber-300">{grandTotals.pending}</td>
                    <td className="px-4 py-4 text-center font-bold text-blue-300">{grandTotals.inProgress}</td>
                    <td className="px-4 py-4 text-center font-bold text-emerald-300">{grandTotals.resolved}</td>
                    <td className="px-4 py-4 text-center font-bold text-red-300">{grandTotals.rejected}</td>
                    <td className="px-4 py-4 text-center font-bold text-gray-300">
                      {grandTotals.total > 0 ? Math.round((grandTotals.resolved / grandTotals.total) * 100) : 0}%
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Ministry Cards */}
          {ministryLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {[1, 2, 3, 4, 5, 6].map(i => <SkeletonCard key={i} />)}
            </div>
          ) : filteredMinistries.length === 0 && !ministryError ? (
            <div className="text-center py-20 text-gray-400">
              <div className="text-5xl mb-4">🔍</div>
              <div className="text-lg font-semibold">No ministry found</div>
              <div className="text-sm mt-1">
                {search ? 'Try a different search term' : 'No ministry data available from backend yet'}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {filteredMinistries.map((ministry, idx) => (
                <MinistryCard key={ministry.id} ministry={ministry} index={idx} />
              ))}
            </div>
          )}
        </div>

        <div className="mt-10 text-center text-gray-400 text-xs">
          Data is fetched live from the Government Grievance Management System (GHSF) database.
        </div>
      </div>
    </div>
  );
};

export default PublicStatsPage;
